import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  CloudUpload,
  CancelOutlined,
  DeleteOutline,
  ImageOutlined,
  Replay,
  VideoFileOutlined,
} from "@mui/icons-material";
import { mediaApi } from "./mediaApi";
import SecureMediaPreview from "./SecureMediaPreview";
import {
  canUploadMediaAsset,
  formatFileSize,
  isAssetUploadActive,
  normalizeUploadContentType,
  sha256File,
  uploadToSignedUrl,
  validateUploadFile,
} from "./media.utils";

const steps = {
  hashing: "Calculating secure SHA-256 checksum…",
  authorizing: "Requesting a short-lived upload authorization…",
  uploading: "Uploading directly to secure storage…",
  verifying: "Backend is inspecting and verifying the file…",
  complete: "Upload verified by the backend.",
};

const MAX_PROCESSING_POLLS = 60;

export const UPLOAD_UI_STATES = Object.freeze({
  EMPTY: "EMPTY",
  SELECTED: "SELECTED",
  UPLOADING: "UPLOADING",
  VERIFYING: "VERIFYING",
  PROCESSING: "PROCESSING",
  READY: "READY",
  FAILED: "FAILED",
  CANCELLING: "CANCELLING",
});

const MediaUploadPanel = ({ assetType, media, onVerified, onStateChange }) => {
  const inputRef = useRef(null);
  const completingRef = useRef(false);
  const mountedRef = useRef(true);
  const uploadAttemptRef = useRef(0);
  const activeUploadRef = useRef(null);
  const cancellingRef = useRef(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [phase, setPhase] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [pollingPaused, setPollingPaused] = useState(false);
  const [focalPoint, setFocalPoint] = useState(media?.poster?.focalPoint || "center");
  const [regenerating, setRegenerating] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [retryingProcessing, setRetryingProcessing] = useState(false);

  const isPoster = assetType === "poster";
  const isShort = media?.kind === "short";
  const mediaId = media?._id;
  const verified = Boolean(
    isPoster ? media?.poster?.verifiedAt : media?.video?.verifiedAt
  );
  const uploadState = media?.assetUploads?.[assetType] || {};
  const replacementActive = isAssetUploadActive(media, assetType);
  const replacementFailed = uploadState.status === "failed";
  const busy = ["hashing", "authorizing", "uploading", "verifying", "cancelling"].includes(phase);
  const awaitingVerification = phase === "waiting";
  const videoProcessing = !isPoster && (
    replacementActive || (media?.assetStatus === "processing" && !verified)
  );
  const processingActive = videoProcessing || phase === "processing";
  const processingProgress = media?.video?.processingProgress || {};
  const rawProcessingPercent = processingProgress.percent;
  const processingIndeterminate = processingProgress.indeterminate === true ||
    rawProcessingPercent === null || rawProcessingPercent === undefined;
  const processingPercentValue = Number(rawProcessingPercent);
  const processingPercent = !processingIndeterminate && Number.isFinite(processingPercentValue)
    ? Math.min(100, Math.max(0, Math.round(processingPercentValue)))
    : null;
  const processingMessage = processingProgress.message ||
    "The backend is preparing the uploaded video.";
  const processingRetryEligible = !isPoster &&
    processingProgress.retryEligible === true && uploadState.status === "failed";
  const sourceOrientation = media?.video?.sourceMetadata?.orientation || "";
  const expectedOrientation = media?.video?.processingPlan?.expectedOrientation ||
    (isShort ? "portrait" : "landscape");
  const orientationSuitable = media?.video?.processingPlan?.orientationSuitable;
  const orientationMessage = sourceOrientation
    ? orientationSuitable === true
      ? `${sourceOrientation[0].toUpperCase()}${sourceOrientation.slice(1)} orientation detected — no orientation change needed.`
      : orientationSuitable === false
      ? `${sourceOrientation[0].toUpperCase()}${sourceOrientation.slice(1)} source preserved; expected ${expectedOrientation} presentation without automatic cropping.`
      : `${sourceOrientation[0].toUpperCase()}${sourceOrientation.slice(1)} orientation detected.`
    : "";
  const canUpload = canUploadMediaAsset(media, assetType);
  const currentFileName = file?.name || uploadState.fileName ||
    (isPoster ? media?.poster?.fileName : media?.video?.fileName) || "";
  const uiState = phase === "cancelling"
    ? UPLOAD_UI_STATES.CANCELLING
    : ["hashing", "authorizing", "uploading"].includes(phase)
    ? UPLOAD_UI_STATES.UPLOADING
    : ["verifying", "waiting"].includes(phase)
    ? UPLOAD_UI_STATES.VERIFYING
    : processingActive
    ? UPLOAD_UI_STATES.PROCESSING
    : phase === "failed" || (error && !file)
    ? UPLOAD_UI_STATES.FAILED
    : file
    ? UPLOAD_UI_STATES.SELECTED
    : verified
    ? UPLOAD_UI_STATES.READY
    : UPLOAD_UI_STATES.EMPTY;
  const statusClass = uiState === UPLOAD_UI_STATES.READY
    ? "ready"
    : [UPLOAD_UI_STATES.UPLOADING, UPLOAD_UI_STATES.VERIFYING,
        UPLOAD_UI_STATES.PROCESSING, UPLOAD_UI_STATES.CANCELLING].includes(uiState)
    ? "processing"
    : uiState === UPLOAD_UI_STATES.FAILED
    ? "failed"
    : "pending";
  const statusLabel = {
    [UPLOAD_UI_STATES.EMPTY]: "Not uploaded",
    [UPLOAD_UI_STATES.SELECTED]: "Ready to upload",
    [UPLOAD_UI_STATES.UPLOADING]: "Uploading",
    [UPLOAD_UI_STATES.VERIFYING]: "Verifying",
    [UPLOAD_UI_STATES.PROCESSING]: isPoster ? "Preparing thumbnail" : "Preparing video",
    [UPLOAD_UI_STATES.READY]: "Ready",
    [UPLOAD_UI_STATES.FAILED]: "Failed",
    [UPLOAD_UI_STATES.CANCELLING]: "Canceling",
  }[uiState];

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  useEffect(() => {
    if (onStateChange) {
      onStateChange(assetType, {
        busy,
        phase,
        processing: processingActive,
        uiState,
      });
    }
  }, [assetType, busy, onStateChange, phase, processingActive, uiState]);

  useEffect(() => {
    if (!file || !isPoster) {
      setPreviewUrl("");
      return undefined;
    }
    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);
    return () => URL.revokeObjectURL(localUrl);
  }, [file, isPoster]);

  useEffect(() => {
    if (!videoProcessing || !mediaId) return undefined;
    let stopped = false;
    let attempts = 0;
    let timer;
    const refresh = async () => {
      attempts += 1;
      try {
        const { media: refreshed } = await mediaApi.get(mediaId);
        if (stopped) return;
        const refreshedUpload = refreshed.assetUploads?.video || {};
        const refreshedActive = isAssetUploadActive(refreshed, "video");
        if (refreshedUpload.status === "failed" || (refreshed.assetStatus === "failed" && !refreshed.video?.verifiedAt)) {
          setPhase("failed");
          setError(
            refreshedUpload.errorMessage ||
              refreshed.assetError?.message ||
              (refreshed.video?.verifiedAt
                ? "We couldn't replace the video. Your previous video is still available."
                : "We couldn't prepare this video for playback. Try uploading it again or choose another file.")
          );
        } else if (!refreshedActive && refreshed.video?.verifiedAt) {
          setPhase("complete");
          setError("");
        } else {
          setPhase("processing");
          setError("");
          if (attempts >= MAX_PROCESSING_POLLS) {
            setPollingPaused(true);
            window.clearInterval(timer);
          }
        }
        onVerified(refreshed, { statusOnly: true });
      } catch {
        // Keep showing the durable processing state; the page-level refresh remains available.
      }
    };
    setPhase("processing");
    setPollingPaused(false);
    timer = window.setInterval(refresh, 5000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [mediaId, onVerified, videoProcessing]);

  useEffect(() => {
    if (!verified || phase !== "complete") return;
    setFile(null);
    setError("");
  }, [phase, verified]);

  const chooseFile = (event) => {
    const nextFile = event.target.files?.[0] || null;
    event.target.value = "";
    if (!nextFile) return;
    const validationError = validateUploadFile(nextFile, assetType);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (["hashing", "authorizing", "uploading", "verifying"].includes(phase)) {
      setConfirmation({ action: "replace-active", file: nextFile });
      return;
    }
    if (verified || replacementActive) {
      setConfirmation({ action: "replace", file: nextFile });
      return;
    }
    setError("");
    setProgress(0);
    setPhase("idle");
    setFile(nextFile);
  };

  const confirmReplacement = async () => {
    const nextFile = confirmation?.file;
    const replaceActive = confirmation?.action === "replace-active";
    setConfirmation(null);
    if (!nextFile) return;
    if (replaceActive) {
      await cancelUpload({ nextFile, startReplacement: true });
      return;
    }
    setFile(nextFile);
    setError("");
    setProgress(0);
    setPhase("idle");
  };

  const removeAsset = async () => {
    if (!mediaId || media.status !== "draft" || removing) return;
    setConfirmation(null);
    setRemoving(true);
    setError("");
    try {
      await mediaApi.removeAsset(mediaId, assetType);
      const { media: refreshed } = await mediaApi.get(mediaId);
      setFile(null);
      setPhase("idle");
      setProgress(0);
      onVerified(refreshed);
    } catch (removeError) {
      setError(
        removeError.message ||
          `The ${isPoster ? "thumbnail" : "video"} couldn't be removed. Please try again.`
      );
    } finally {
      setRemoving(false);
    }
  };

  const upload = async (selectedFile = file) => {
    const validationError = validateUploadFile(selectedFile, assetType);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!canUpload || activeUploadRef.current || phase === "cancelling") return;

    const attemptId = ++uploadAttemptRef.current;
    const controller = new AbortController();
    const context = {
      attemptId,
      authorization: null,
      controller,
      intentPromise: null,
    };
    activeUploadRef.current = context;
    setError("");
    setProgress(0);
    try {
      setPhase("hashing");
      const checksumSha256 = await sha256File(selectedFile);
      if (!mountedRef.current || attemptId !== uploadAttemptRef.current) return;
      setPhase("authorizing");
      context.intentPromise = mediaApi.uploadIntent(media._id, {
        assetType,
        contentType: normalizeUploadContentType(selectedFile, assetType),
        sizeBytes: selectedFile.size,
        checksumSha256,
        fileName: selectedFile.name,
        ...(isPoster && isShort ? { focalPoint } : {}),
      });
      const { upload: authorization } = await context.intentPromise;
      context.authorization = authorization;
      if (!mountedRef.current || attemptId !== uploadAttemptRef.current) return;
      setPhase("uploading");
      await uploadToSignedUrl({
        upload: authorization,
        file: selectedFile,
        signal: controller.signal,
        onProgress: (value) => {
          if (mountedRef.current && attemptId === uploadAttemptRef.current) setProgress(value);
        },
      });

      if (!mountedRef.current || attemptId !== uploadAttemptRef.current || completingRef.current) return;
      completingRef.current = true;
      setPhase("verifying");
      const completion = await mediaApi.uploadComplete(media._id, authorization.uploadSessionId);
      const { media: refreshed } = await mediaApi.get(media._id);
      if (!mountedRef.current || attemptId !== uploadAttemptRef.current) return;
      setProgress(100);
      setPhase(completion.processing === true ? "processing" : "complete");
      setFile(null);
      onVerified(refreshed);
    } catch (uploadError) {
      if (!mountedRef.current || attemptId !== uploadAttemptRef.current || uploadError.code === "UPLOAD_CANCELLED") return;
      try {
        const { media: refreshed } = await mediaApi.get(media._id);
        if (!mountedRef.current) return;
        onVerified(refreshed);
        if (
          !isPoster &&
          (uploadError.code === "ASSET_PROCESSING" ||
            (refreshed.assetStatus === "processing" && !refreshed.video?.verifiedAt))
        ) {
          setPhase("processing");
          setFile(null);
          setError("");
          return;
        }
      } catch {
        // Preserve the actionable upload error; the page-level Refresh action remains available.
      }
      if (uploadError.code === "ASSET_VERIFYING") {
        setPhase("waiting");
        setError(uploadError.message);
        return;
      }
      setPhase("failed");
      setError(uploadError.message || "The upload failed. Request a new upload and retry.");
    } finally {
      completingRef.current = false;
      if (activeUploadRef.current?.attemptId === attemptId) activeUploadRef.current = null;
    }
  };

  const cancelUpload = async ({ nextFile = null, startReplacement = false } = {}) => {
    if (cancellingRef.current) return false;
    const context = activeUploadRef.current;
    if (!context) return false;
    cancellingRef.current = true;
    ++uploadAttemptRef.current;
    setPhase("cancelling");
    setError("");
    context.controller.abort();
    try {
      let authorization = context.authorization;
      if (!authorization && context.intentPromise) {
        try {
          const result = await context.intentPromise;
          authorization = result.upload;
        } catch {
          authorization = null;
        }
      }
      if (authorization?.uploadSessionId) {
        await mediaApi.uploadCancel(media._id, authorization.uploadSessionId);
      }
      const { media: refreshed } = await mediaApi.get(media._id);
      if (!mountedRef.current) return true;
      onVerified(refreshed, { statusOnly: true });
      activeUploadRef.current = null;
      setProgress(0);
      setFile(nextFile);
      setPhase("idle");
      setError("");
      if (startReplacement && nextFile) upload(nextFile);
      return true;
    } catch (cancelError) {
      let refreshedMedia = null;
      try {
        const { media: refreshed } = await mediaApi.get(media._id);
        refreshedMedia = refreshed;
        if (mountedRef.current) onVerified(refreshed, { statusOnly: true });
      } catch {
        // Keep the cancellation failure visible; Refresh status remains available.
      }
      if (mountedRef.current) {
        if (["UPLOAD_ALREADY_COMPLETED", "UPLOAD_SUPERSEDED"].includes(cancelError.code)) {
          activeUploadRef.current = null;
          setFile(null);
          setPhase(
            isAssetUploadActive(refreshedMedia, assetType)
              ? "processing"
              : Boolean(isPoster ? refreshedMedia?.poster?.verifiedAt : refreshedMedia?.video?.verifiedAt)
              ? "complete"
              : "idle"
          );
        } else {
          setPhase("verifying");
        }
        setError(cancelError.message || "The upload could not be cancelled safely. Refresh status and try again.");
      }
      return false;
    } finally {
      cancellingRef.current = false;
    }
  };

  const regeneratePoster = async () => {
    if (!mediaId || !isPoster || !isShort || regenerating) return;
    setRegenerating(true);
    setError("");
    try {
      const { media: refreshed } = await mediaApi.regeneratePoster(mediaId, focalPoint);
      onVerified(refreshed);
    } catch (regenerationError) {
      setError(regenerationError.message || "The portrait poster could not be generated.");
    } finally {
      setRegenerating(false);
    }
  };

  const retryVideoProcessing = async () => {
    if (!mediaId || !processingRetryEligible || retryingProcessing) return;
    setRetryingProcessing(true);
    setError("");
    try {
      await mediaApi.retryProcessing(mediaId);
      const { media: refreshed } = await mediaApi.get(mediaId);
      setPhase("processing");
      onVerified(refreshed, { statusOnly: true });
    } catch (retryError) {
      setPhase("failed");
      setError(retryError.message || "Video processing could not be retried.");
    } finally {
      setRetryingProcessing(false);
    }
  };

  const normalizedShortPoster = Boolean(
    isShort &&
      media?.poster?.verifiedAt &&
      Number(media?.poster?.width) * 16 === Number(media?.poster?.height) * 9
  );

  return (
    <>
    <Box className="media-upload-panel">
      <Box className="media-upload-heading">
        <Box>
          <Typography variant="subtitle1" component="h3">
            {isPoster ? "Thumbnail image" : "Video"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {isPoster
              ? "JPEG or PNG, up to 10 MB"
              : isShort
              ? "MP4 or MOV up to 500 MB · original framing preserved · optimized only when needed"
              : "MP4 or MOV up to 500 MB · optimized for browser playback only when needed"}
          </Typography>
        </Box>
        <span
          className={`media-status media-status--${statusClass}`}
        >
          {statusLabel}
        </span>
      </Box>

      {!verified && !file && !processingActive && !previewUrl && (
        <Box className="media-upload-empty">
          {isPoster ? <ImageOutlined /> : <VideoFileOutlined />}
          <Typography variant="subtitle2">
            {isPoster ? "Choose a thumbnail" : "Upload your video"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {isPoster
              ? `JPEG or PNG · up to 10 MB${isShort ? " · converted to 1080×1920" : ""}`
              : "Choose an MP4 or MOV file · up to 500 MB"}
          </Typography>
        </Box>
      )}

      {previewUrl && (
        <img className="media-upload-poster-preview" src={previewUrl} alt="Selected poster preview" />
      )}

      {verified && !previewUrl && (
        <Box className="media-upload-preview">
          <SecureMediaPreview assetType={assetType} media={media} compact />
        </Box>
      )}
      {!media?._id && <Alert severity="info">Save the draft before uploading files.</Alert>}
      {media?._id && media.status !== "draft" && (
        <Alert severity="info">
          This post stays live with its current {isPoster ? "thumbnail" : "video"} until a replacement is verified.
        </Alert>
      )}
      {processingActive && (
        <Alert severity="info" className="media-processing-alert">
          <Box className="media-processing-summary">
            <strong>Processing video…</strong>
            {processingPercent !== null && (
              <span className="media-processing-percent">{processingPercent}%</span>
            )}
          </Box>
          <LinearProgress
            className="media-processing-bar"
            variant={processingPercent === null ? "indeterminate" : "determinate"}
            value={processingPercent === null ? undefined : processingPercent}
            aria-label={
              processingPercent === null
                ? "Video processing progress"
                : `Video processing ${processingPercent}% complete`
            }
          />
          <Typography variant="body2" component="p" className="media-processing-message">
            {processingMessage}
          </Typography>
          {orientationMessage && (
            <Typography variant="caption" component="p">
              {orientationMessage}
            </Typography>
          )}
          <Typography variant="caption" component="p">
            We're preparing your {isShort ? "Short" : "Video"}. You may leave this page; progress is saved and refreshes automatically.
          </Typography>
          {currentFileName && <Typography variant="caption" component="p">File: {currentFileName}</Typography>}
        </Alert>
      )}
      {pollingPaused && (
        <Alert severity="info">Automatic status refresh paused after five minutes. Use Refresh status before publishing.</Alert>
      )}
      {error && <Alert severity={awaitingVerification ? "info" : "error"}>{error}</Alert>}
      {!error && replacementFailed && verified && (
        <Alert severity="warning">
          {uploadState.errorMessage || `We couldn't replace the ${isPoster ? "thumbnail" : "video"}. Your previous ${isPoster ? "thumbnail" : "video"} is still available.`}
        </Alert>
      )}
      {!isPoster && orientationMessage && !processingActive && (
        <Alert severity={orientationSuitable === false ? "info" : "success"}>
          {orientationMessage}
        </Alert>
      )}

      {isPoster && isShort && media?._id && (
        <Box className="media-poster-conversion-controls">
          <TextField
            select
            size="small"
            label="Portrait crop focus"
            value={focalPoint}
            onChange={(event) => setFocalPoint(event.target.value)}
            disabled={busy || regenerating || media.status === "archived"}
            helperText="The backend crops without stretching and creates a real 1080×1920 poster."
          >
            {["center", "top", "bottom", "left", "right"].map((position) => (
              <MenuItem key={position} value={position}>{position[0].toUpperCase() + position.slice(1)}</MenuItem>
            ))}
          </TextField>
          {verified && (!normalizedShortPoster || media.poster?.focalPoint !== focalPoint) && (
            <Button variant="outlined" startIcon={regenerating ? <CircularProgress size={16} /> : <Replay />}
              onClick={regeneratePoster} disabled={regenerating || media.status !== "draft"}>
              {regenerating ? "Generating…" : normalizedShortPoster ? "Regenerate crop" : "Generate 9:16 poster"}
            </Button>
          )}
        </Box>
      )}

      {busy && phase !== "cancelling" && (
        <Box className="media-upload-progress" aria-live="polite">
          <Box display="flex" alignItems="center" mb={1}>
            {phase !== "uploading" && <CircularProgress size={18} />}
            <Typography variant="body2" ml={1}>{steps[phase]}</Typography>
          </Box>
          {phase === "uploading" && (
            <>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="caption">{progress}% uploaded</Typography>
            </>
          )}
        </Box>
      )}
      {phase === "cancelling" && (
        <Alert severity="info" icon={<CircularProgress size={18} />}>
          Cancelling the upload and confirming the latest server state…
        </Alert>
      )}
      {phase === "complete" && <Alert severity="success">{steps.complete}</Alert>}

      <Box className="media-upload-actions">
        <input
          ref={inputRef}
          type="file"
          hidden
          accept={isPoster ? "image/jpeg,image/png" : ".mp4,.mov,video/mp4,video/quicktime"}
          onChange={chooseFile}
          aria-label={`Choose ${assetType} file`}
        />
        <Button
          variant="outlined"
          onClick={() => inputRef.current?.click()}
          disabled={!canUpload || phase === "cancelling" || removing}
        >
          {uiState === UPLOAD_UI_STATES.EMPTY
            ? "Choose"
            : [UPLOAD_UI_STATES.SELECTED, UPLOAD_UI_STATES.UPLOADING,
                UPLOAD_UI_STATES.VERIFYING].includes(uiState)
            ? "Change"
            : "Replace"} {isPoster ? "thumbnail" : "video"}
        </Button>
        {file && !busy && (
          <Button
            variant="contained"
            startIcon={phase === "failed" || awaitingVerification ? <Replay /> : <CloudUpload />}
            onClick={() => upload(file)}
            disabled={!canUpload || removing}
          >
            {awaitingVerification
              ? "Retry verification"
              : phase === "failed"
              ? "Try Again"
              : verified || replacementActive
              ? `Upload replacement ${isPoster ? "thumbnail" : "video"}`
              : `Upload ${isPoster ? "thumbnail" : "video"}`}
          </Button>
        )}
        {processingRetryEligible && (
          <Button
            variant="outlined"
            startIcon={retryingProcessing ? <CircularProgress size={16} /> : <Replay />}
            onClick={retryVideoProcessing}
            disabled={retryingProcessing || busy}
          >
            {retryingProcessing ? "Retrying…" : "Retry processing"}
          </Button>
        )}
        {busy && phase !== "cancelling" && (
          <Button
            color="warning"
            variant="outlined"
            startIcon={<CancelOutlined />}
            onClick={() => cancelUpload()}
          >
            Cancel Upload
          </Button>
        )}
        {file && !busy && (
          <Button
            variant="text"
            onClick={() => {
              setFile(null);
              setProgress(0);
              setPhase("idle");
              setError("");
            }}
            disabled={removing}
          >
            Remove Selection
          </Button>
        )}
        {media?.status === "draft" && (verified || replacementActive) && (
          <Button
            color="error"
            variant="text"
            startIcon={removing ? <CircularProgress size={16} /> : <DeleteOutline />}
            onClick={() => setConfirmation({ action: "remove" })}
            disabled={busy || removing}
          >
            {removing ? "Removing…" : `Remove ${isPoster ? "thumbnail" : "video"}`}
          </Button>
        )}
        {file && (
          <Box className="media-selected-file" aria-live="polite">
            <Typography variant="body2" component="strong">{file.name}</Typography>
            <Typography variant="caption" color="textSecondary">
            {formatFileSize(file.size)} · {normalizeUploadContentType(file, assetType) || file.type || "Unknown type"} · {busy ? "upload in progress" : "ready to upload"}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
    <Dialog
      open={Boolean(confirmation)}
      onClose={() => !removing && setConfirmation(null)}
      aria-labelledby="media-asset-confirmation-title"
    >
      <DialogTitle id="media-asset-confirmation-title">
        {confirmation?.action === "remove"
          ? `Remove ${isPoster ? "thumbnail" : "video"}?`
          : `Replace ${isPoster ? "thumbnail" : "video"}?`}
      </DialogTitle>
      <DialogContent>
        <Typography>
          {confirmation?.action === "remove"
            ? `This removes the ${isPoster ? "thumbnail" : "video"} from this draft. Your title and other content details will not change.`
            : `Your current ${isPoster ? "thumbnail" : "video"} will remain available until the new file is fully prepared and verified.`}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmation(null)} disabled={removing}>Cancel</Button>
        <Button
          color={confirmation?.action === "remove" ? "error" : "primary"}
          variant="contained"
          onClick={confirmation?.action === "remove" ? removeAsset : confirmReplacement}
          disabled={removing}
        >
          {confirmation?.action === "remove" ? "Remove" : "Choose replacement"}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default MediaUploadPanel;
